import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { filter, first, map, switchMap, tap } from 'rxjs/operators';
import { ApplicationService } from '.';
import { PendingProviderSelection } from '../../../core/services';
import { AuthService } from '../../auth/services';
import { Application } from '../models';

@Injectable({
    providedIn: 'root'
})
export class CurrentApplicationService {
    latest = new BehaviorSubject<Application | undefined>(undefined);
    currentApplicationId: number | undefined;

    constructor(
        private readonly _applicationService: ApplicationService,
        private readonly _auth: AuthService
    ) {}

    load() {
        this.load$().subscribe();
    }

    /**
     * The awaitable form of load(), for callers that must know the application has arrived —
     * ApplicationEntryGuard needs it before any page renders.  Mirrors ClientService.load$().
     *
     * A staged selection passed here is attached by the API in the same call, so the application
     * is never returned without the student's choice on it.
     */
    load$(
        selection?: PendingProviderSelection
    ): Observable<Application | undefined> {
        return this._auth.user$.pipe(
            filter((user) => user != null),
            first(),
            switchMap((user) => {
                if (user?.userId) {
                    return this._applicationService.getCurrentApplication(
                        user.userId,
                        selection
                    );
                } else {
                    return of(undefined);
                }
            }),
            // One representation of "no application".  A user without an id, or an empty response
            // body, otherwise emits null through an observable declared to carry an Application,
            // and publishes it to `latest`, which is typed Application | undefined.
            map((application?: Application) => application ?? undefined),
            tap((application) => {
                this.currentApplicationId = application?.applicationId;
                this.latest.next(application);
            })
        );
    }

    reset() {
        this.latest = new BehaviorSubject<Application | undefined>(undefined);
    }
}
